// frontend/src/store/userStore.js
// Store de Zustand para gestionar el estado de los usuarios.

import { create } from 'zustand';
import {
  getAllUsers as apiGetAllUsers,
  createUser as apiCreateUser,
  updateUser as apiUpdateUser,
  deleteUser as apiDeleteUser,
} from '../api/user';
import { useAuthStore } from './authStore';
import { useUIStore } from './uiStore';

/**
 * @typedef {object} UserStoreState
 * @property {Array<object>} users - Lista de usuarios.
 * @property {boolean} isLoadingUsers - Indica si se están cargando los usuarios.
 * @property {function()} fetchUsers - Carga usuarios del backend.
 * @property {function(object)} addUser - Añade un nuevo usuario.
 * @property {function(string, object)} updateUser - Actualiza un usuario existente.
 * @property {function(string)} deleteUser - Elimina un usuario.
 */

export const useUserStore = create((set) => ({
  users: [],
  isLoadingUsers: false,

  /**
   * @function fetchUsers
   * @description Carga la lista de usuarios desde el backend.
   */
  fetchUsers: async () => {
    const token = useAuthStore.getState().token;
    if (!token) return;

    set({ isLoadingUsers: true });
    try {
      const users = await apiGetAllUsers(token);
      set({ users });
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al cargar usuarios.', 'error');
      set({ users: [] });
    } finally {
      set({ isLoadingUsers: false });
    }
  },

  /**
   * @function addUser
   * @description Añade un nuevo usuario y actualiza el store.
   * @param {object} userData - Datos del nuevo usuario.
   */
  addUser: async (userData) => {
    const token = useAuthStore.getState().token;
    if (!token) {
        useUIStore.getState().showTemporaryMessage('No autenticado para añadir usuario.', 'error');
        return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      const { user: newUser } = await apiCreateUser(userData, token);
      set((state) => ({ users: [...state.users, newUser] }));
      useUIStore.getState().showTemporaryMessage('Usuario creado con éxito.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al crear usuario.', 'error');
      throw error;
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  /**
   * @function updateUser
   * @description Actualiza un usuario y el store.
   * @param {string} userId - ID del usuario a actualizar.
   * @param {object} userData - Datos actualizados.
   */
  updateUser: async (userId, userData) => {
    const token = useAuthStore.getState().token;
    if (!token) {
        useUIStore.getState().showTemporaryMessage('No autenticado para actualizar usuario.', 'error');
        return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      const { user: updatedUser } = await apiUpdateUser(userId, userData, token);
      set((state) => ({
        users: state.users.map((u) => (u.id === userId ? updatedUser : u)),
      }));
      useUIStore.getState().showTemporaryMessage('Usuario actualizado con éxito.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al actualizar usuario.', 'error');
      throw error;
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },

  /**
   * @function deleteUser
   * @description Elimina un usuario y lo quita del store.
   * @param {string} userId - ID del usuario a eliminar.
   */
  deleteUser: async (userId) => {
    const token = useAuthStore.getState().token;
    if (!token) {
        useUIStore.getState().showTemporaryMessage('No autenticado para eliminar usuario.', 'error');
        return;
    }

    useUIStore.getState().setIsLoading(true);
    try {
      await apiDeleteUser(userId, token);
      set((state) => ({
        users: state.users.filter((u) => u.id !== userId),
      }));
      useUIStore.getState().showTemporaryMessage('Usuario eliminado con éxito.', 'success');
    } catch (error) {
      useUIStore.getState().showTemporaryMessage(error.message || 'Error al eliminar usuario.', 'error');
    } finally {
      useUIStore.getState().setIsLoading(false);
    }
  },
}));
